import { Checkbox, Field, Input, InputNumber, Select, Textarea } from '@repo/go-ui';
import { hasValue } from '@repo/utils';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { FactTrajectoryEntity } from '../../service/entities';
import { isAzimuthReference } from '../../utils/is-azimuth-reference';

import { formatTrajectoryMeasure, formatTrajectoryText } from './format-trajectory-measure';
import styles from './trajectory-list-sidebar.module.scss';
import type { TrajectoryListSidebarStore } from './trajectory-list-sidebar.store';

const EMPTY = '—';
const KINDS_KEY = 'ListSidebar.kinds';

function normalizeTitle(value: string | undefined | null): string {
  return (value ?? '').trim();
}

type TReferencePointLike = {
  baseReferencePoint?: { uuid?: string | null } | Record<string, unknown> | null;
};

function resolveReferencePointUuid(point: TReferencePointLike | null | undefined): string | null {
  const base = point?.baseReferencePoint;

  if (!base || typeof base !== 'object' || !('uuid' in base)) {
    return null;
  }

  const uuid = base.uuid;

  if (typeof uuid === 'string' && uuid.length > 0) {
    return uuid;
  }

  return null;
}

function datumOption(
  datum: { name?: string | null; kind?: string | null; baseReferencePoint?: unknown } | null | undefined,
  t: (key: string, options?: { defaultValue: string }) => string,
): { value: string; label: string } | null {
  const base = datum?.baseReferencePoint;
  const uuid = resolveReferencePointUuid({
    baseReferencePoint: base && typeof base === 'object' ? (base as { uuid?: string | null }) : null,
  });

  if (!uuid) {
    return null;
  }

  const name = normalizeTitle(datum?.name);
  const kind = normalizeTitle(datum?.kind);
  const kindLabel = kind ? t(`${KINDS_KEY}.${kind}`, { defaultValue: kind }) : '';

  return { value: uuid, label: name || kindLabel || uuid };
}

type Props = {
  store: TrajectoryListSidebarStore;
  trajectory: FactTrajectoryEntity | null;
  parentTitle?: string | null;
  isEditing?: boolean;
  disabled?: boolean;
};

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

export const TrajectoryDetails = observer(function TrajectoryDetails({
  store,
  trajectory,
  parentTitle,
  isEditing = false,
  disabled = false,
}: Props) {
  const { t } = useTranslation();

  if (!trajectory) {
    return <div className={styles.detailsEmpty}>{t('ListSidebar.noSelection')}</div>;
  }

  const { data } = trajectory;
  const isActive = Boolean(data.active);
  const isFinal = Boolean(data.finalTraj);
  const aziRefValue = isAzimuthReference(data.aziRef) ? data.aziRef : null;
  const referencePointOptions = (() => {
    const options: { value: string; label: string }[] = [];
    const seen = new Set<string>();

    for (const point of [...store.wellboreReferencePoints, data.defaultMdDatum, data.defaultTvdDatum]) {
      const option = datumOption(point, t);

      if (!option || seen.has(option.value)) {
        continue;
      }

      seen.add(option.value);
      options.push(option);
    }

    return options;
  })();
  const resolveDatumLabel = (datumKind: 'md' | 'tvd') => {
    const datum = datumKind === 'md' ? data.defaultMdDatum : data.defaultTvdDatum;
    const uuid = datumKind === 'md' ? store.getDefaultMdDatumUuid() : store.getDefaultTvdDatumUuid();
    const fromOptions = hasValue(uuid) ? referencePointOptions.find((option) => option.value === uuid)?.label : null;

    if (fromOptions && fromOptions !== uuid) {
      return fromOptions;
    }

    const fromName = normalizeTitle(datum?.name);

    if (fromName) {
      return fromName;
    }

    const fromKind = normalizeTitle(datum?.kind);

    if (fromKind) {
      return t(`${KINDS_KEY}.${fromKind}`, { defaultValue: fromKind });
    }

    if (hasValue(uuid)) {
      return uuid;
    }

    return EMPTY;
  };

  return (
    <div className={styles.details}>
      <section className={styles.section}>
        {isEditing ? (
          <>
            <Field label={t('ListSidebar.fields.name')} size="s" className={styles.editField}>
              <Input
                value={data.citation.title ?? ''}
                disabled={disabled}
                onChange={(e) => store.setDraftTitle(e.currentTarget.value)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.description')} size="s" className={styles.editField}>
              <Textarea
                value={data.citation.description ?? ''}
                disabled={disabled}
                rows={1}
                minRows={1}
                maxRows={3}
                autoHeight
                allowClear
                className={styles.descriptionTextarea}
                onChange={(value) => store.setDraftDescription(value)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.organization')} size="s" className={styles.editField}>
              <Select
                allowClear
                value={data.organizationId ?? null}
                options={store.uomOptions.organizationOptions}
                placeholder={t('common:chooseValue')}
                disabled={disabled || store.uomOptions.areOptionsLoading}
                showSearch
                optionFilterProp="label"
                onChange={(value) => store.setOrganizationId(typeof value === 'number' ? value : null)}
              />
            </Field>
          </>
        ) : (
          <>
            <DetailField label={t('ListSidebar.fields.name')} value={formatTrajectoryText(data.citation.title)} />
            <DetailField
              label={t('ListSidebar.fields.description')}
              value={formatTrajectoryText(data.citation.description)}
            />
            <DetailRow label={t('ListSidebar.fields.organization')} value={store.resolveOrganizationLabel(data)} />
          </>
        )}
        <div className={styles.checkboxes}>
          <Checkbox
            checked={isActive}
            disabled={!isEditing || disabled}
            size="s"
            onChange={(checked) => store.setActive(checked)}
          >
            {t('ListSidebar.active')}
          </Checkbox>
          <Checkbox
            checked={isFinal}
            disabled={!isEditing || disabled}
            size="s"
            onChange={(checked) => store.setFinalTraj(checked)}
          >
            {t('ListSidebar.final')}
          </Checkbox>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.tieIn')}</h3>
        {isEditing ? (
          <>
            <Field label={t('ListSidebar.fields.parentTrajectory')} size="s" className={styles.editField}>
              <Select
                allowClear
                value={data.parentTrajectoryId ?? null}
                options={store.parentTrajectoryCandidates.map((item) => ({
                  value: item.id,
                  label: item.title || t('ListSidebar.untitled'),
                }))}
                placeholder={t('common:chooseValue')}
                disabled={disabled || !store.hasParentWellbore}
                onChange={(value) => store.setParentTrajectoryId(typeof value === 'number' ? value : null)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.mdTieOn')} size="s" className={styles.editField}>
              <InputNumber
                value={store.getMdTieOnValue()}
                placeholder={t('common:chooseValue')}
                disabled={disabled || !store.hasParentWellbore}
                onChange={(value) => {
                  if (typeof value !== 'number' || Number.isNaN(value)) {
                    store.setMdTieOn(null);
                    return;
                  }

                  store.setMdTieOn(value);
                }}
              />
            </Field>
          </>
        ) : (
          <>
            <DetailField label={t('ListSidebar.fields.parentTrajectory')} value={formatTrajectoryText(parentTitle)} />
            <DetailField
              label={t('ListSidebar.fields.mdTieOn')}
              value={formatTrajectoryMeasure(data.mdTieOn?.measuredDepth)}
            />
          </>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.azimuth')}</h3>
        {isEditing ? (
          <>
            <Field label={t('ListSidebar.fields.aziRef')} size="s" className={styles.editField}>
              <Select
                value={aziRefValue}
                options={store.aziRefOptions.map((option) => ({
                  value: option.value,
                  label: t(option.label),
                }))}
                placeholder={t('common:chooseValue')}
                disabled={disabled || store.uomOptions.areOptionsLoading}
                onChange={(value) => store.setAziRef(typeof value === 'string' ? value : null)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.gridConUsed')} size="s" className={styles.editField}>
              <InputNumber
                value={store.getAngleFieldValue('gridConUsed')}
                placeholder={t('common:chooseValue')}
                disabled={disabled}
                onChange={(value) => {
                  if (typeof value !== 'number' || Number.isNaN(value)) {
                    store.setGridConUsed(null);
                    return;
                  }

                  store.setGridConUsed(value);
                }}
              />
            </Field>
            <Field label={t('ListSidebar.fields.magDeclUsed')} size="s" className={styles.editField}>
              <InputNumber
                value={store.getAngleFieldValue('magDeclUsed')}
                placeholder={t('common:chooseValue')}
                disabled={disabled}
                onChange={(value) => {
                  if (typeof value !== 'number' || Number.isNaN(value)) {
                    store.setMagDeclUsed(null);
                    return;
                  }

                  store.setMagDeclUsed(value);
                }}
              />
            </Field>
            <Field label={t('ListSidebar.fields.aziVertSect')} size="s" className={styles.editField}>
              <InputNumber
                value={store.getAngleFieldValue('aziVertSect')}
                placeholder={t('common:chooseValue')}
                disabled={disabled}
                onChange={(value) => {
                  if (typeof value !== 'number' || Number.isNaN(value)) {
                    store.setAziVertSect(null);
                    return;
                  }

                  store.setAziVertSect(value);
                }}
              />
            </Field>
          </>
        ) : (
          <>
            <DetailRow
              label={t('ListSidebar.fields.aziRef')}
              value={(() => {
                const labelKey = store.getAziRefLabel(data.aziRef);

                if (labelKey === '—') {
                  return formatTrajectoryText(data.aziRef);
                }

                return t(labelKey);
              })()}
            />
            <DetailRow label={t('ListSidebar.fields.gridConUsed')} value={store.formatAngleField(data.gridConUsed)} />
            <DetailRow label={t('ListSidebar.fields.magDeclUsed')} value={store.formatAngleField(data.magDeclUsed)} />
            <DetailRow label={t('ListSidebar.fields.aziVertSect')} value={store.formatAngleField(data.aziVertSect)} />
          </>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.referencePoint')}</h3>
        {isEditing ? (
          <>
            <Field label={t('ListSidebar.fields.mdDatum')} size="s" className={styles.editField}>
              <Select
                allowClear
                value={store.getDefaultMdDatumUuid()}
                options={referencePointOptions}
                placeholder={t('common:chooseValue')}
                disabled={disabled}
                showSearch
                optionFilterProp="label"
                onChange={(value) => store.setDefaultMdDatum(typeof value === 'string' ? value : null)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.tvdDatum')} size="s" className={styles.editField}>
              <Select
                allowClear
                value={store.getDefaultTvdDatumUuid()}
                options={referencePointOptions}
                placeholder={t('common:chooseValue')}
                disabled={disabled}
                showSearch
                optionFilterProp="label"
                onChange={(value) => store.setDefaultTvdDatum(typeof value === 'string' ? value : null)}
              />
            </Field>
          </>
        ) : (
          <>
            <DetailRow label={t('ListSidebar.fields.mdDatum')} value={resolveDatumLabel('md')} />
            <DetailRow label={t('ListSidebar.fields.tvdDatum')} value={resolveDatumLabel('tvd')} />
          </>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.units')}</h3>
        {isEditing ? (
          <>
            <Field label={t('ListSidebar.fields.depthUnit')} size="s" className={styles.editField}>
              <Select
                value={data.depthUomId ?? null}
                options={store.uomOptions.depthUomOptions}
                placeholder={t('common:chooseValue')}
                disabled={disabled || store.uomOptions.areOptionsLoading}
                onChange={(value) => store.setDepthUomId(typeof value === 'number' ? value : null)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.intensityUnit')} size="s" className={styles.editField}>
              <Select
                value={data.severityUomId ?? null}
                options={store.uomOptions.severityUomOptions}
                placeholder={t('common:chooseValue')}
                disabled={disabled || store.uomOptions.areOptionsLoading}
                onChange={(value) => store.setSeverityUomId(typeof value === 'number' ? value : null)}
              />
            </Field>
            <Field label={t('ListSidebar.fields.coordinatesUnit')} size="s" className={styles.editField}>
              <Select
                value={data.coordinatesUomId ?? null}
                options={store.uomOptions.coordinatesUomOptions}
                placeholder={t('common:chooseValue')}
                disabled={disabled || store.uomOptions.areOptionsLoading}
                onChange={(value) => store.setCoordinatesUomId(typeof value === 'number' ? value : null)}
              />
            </Field>
          </>
        ) : (
          <>
            <DetailRow label={t('ListSidebar.fields.depthUnit')} value={store.resolveDepthUomLabel(data)} />
            <DetailRow label={t('ListSidebar.fields.intensityUnit')} value={store.resolveIntensityUomLabel(data)} />
            <DetailRow label={t('ListSidebar.fields.coordinatesUnit')} value={store.resolveCoordinatesUomLabel(data)} />
          </>
        )}
      </section>
    </div>
  );
});
